/**
 * AgentPreview Component
 * Shows the EXACT system prompt with colored labels on each section.
 * Tracks code blocks (```) to avoid splitting on ## inside file content.
 */

import React, { useState, useMemo } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import type { TFunc } from '@/utils/tfunc';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AgentPreview.css';

interface AgentPreviewProps {
  content: string;
  title?: string;
  subtitle?: string;
  defaultCollapsed?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}

interface Section {
  label: string;
  description: string;
  content: string;
  colorClass: string;
}

function classifyHeader(header: string, parentSection: string, t: TFunc): { label: string; description: string; colorClass: string } | null {
  const h = header.toLowerCase();

  if (h.includes('organizational context'))
    return { label: t('groupAdmin.context.title'), description: t('agentPreview.inheritedFromTenantHierarchy'), colorClass: 'sec-org' };
  if (h === 'rules' || h.includes('important rules') || h.includes('hard agent rules'))
    return { label: t('bots.modal.hardAgentRules'), description: t('agentPreview.platformBehavioralRules'), colorClass: 'sec-rules' };
  if (h.includes('agent system prompt') || h.includes('bot system prompt'))
    return { label: t('agentPreview.agentSystemPrompt'), description: t('agentPreview.yourAgentPersonality'), colorClass: 'sec-prompt' };
  if (h === 'behavioral modes' || h.includes('behavioral modes'))
    return { label: t('agentPreview.behavioralModes'), description: t('agentPreview.modeCatalogPerTurnDirective'), colorClass: 'sec-modes' };
  if (h.includes('knowledge sourcing'))
    return { label: t('agentPreview.knowledgeSourcing'), description: t('agentPreview.autoGeneratedSearchRules'), colorClass: 'sec-rules' };
  if (h.includes('skill routing'))
    return { label: t('agentPreview.skillRoutingGuide'), description: t('agentPreview.autoGeneratedFromSkillActivation'), colorClass: 'sec-routing' };
  if (h.includes('what the root agent sees'))
    return { label: t('agentPreview.whatTheRootAgentSees'), description: t('agentPreview.theOnlyInfoTheRoot'), colorClass: 'sec-routing' };
  if (h.includes('activated skill content') || h.includes('content loaded on activation'))
    return { label: t('agentPreview.activatedSkillContent'), description: t('agentPreview.theFullInstructionsTheAgent'), colorClass: 'sec-prompt' };
  if (h.includes('reference documents'))
    return { label: t('agentPreview.referenceDocuments'), description: t('agentPreview.filesWithAlwaysInContext'), colorClass: 'sec-reference' };
  if (h.includes('knowledge sources'))
    return { label: t('agentPreview.knowledgeSources'), description: t('agentPreview.searchableViaTools'), colorClass: 'sec-knowledge' };
  if (h.includes('your tools'))
    return { label: t('agentPreview.toolsFunctionCalling'), description: t('agentPreview.toolBackedSkills'), colorClass: 'sec-tools' };
  if (h === 'skills' || h.includes('specialized skills'))
    return { label: t('skills.pageTitle'), description: t('agentPreview.behavioralSubAgents'), colorClass: 'sec-skills' };
  if (h.includes('skill instructions'))
    return { label: t('agentPreview.skillInstructions'), description: t('agentPreview.skillSpecificBehavior'), colorClass: 'sec-prompt' };
  if (h.includes('skill reference material'))
    return { label: t('agentPreview.skillReferenceMaterial'), description: t('agentPreview.inlinedBundleContent'), colorClass: 'sec-reference' };
  if (h.includes('skill reference documents'))
    return { label: t('agentPreview.skillReferenceDocuments'), description: t('agentPreview.inlinedSkillFiles'), colorClass: 'sec-reference' };
  if (h.includes('agent personality'))
    return { label: t('agentPreview.agentPersonality'), description: t('agentPreview.inheritedFromRootAgent'), colorClass: 'sec-org' };
  if (h.includes('available files'))
    return { label: t('agentPreview.availableFiles'), description: t('agentPreview.searchableViaTools'), colorClass: 'sec-knowledge' };
  if (h.includes('user preferences'))
    return { label: t('agentPreview.userPreferences'), description: t('agentPreview.adaptivePersonalization'), colorClass: 'sec-user' };
  if (h.includes('persistent memory'))
    return { label: t('agentPreview.persistentMemory'), description: t('agentPreview.crossConversationMemory'), colorClass: 'sec-user' };

  // ### sub-headers — label with parent context
  if (parentSection === 'sec-reference')
    return { label: `File: ${header}`, description: t('agentPreview.inlinedContent'), colorClass: 'sec-reference' };
  if (parentSection === 'sec-knowledge') {
    if (h.includes('direct files'))
      return { label: t('agentPreview.directFiles'), description: t('agentPreview.agentLevelFileDescriptions'), colorClass: 'sec-knowledge' };
    return { label: `Data Bundle: ${header}`, description: t('agentPreview.bundleDescriptions'), colorClass: 'sec-knowledge' };
  }
  if (parentSection === 'sec-tools')
    return { label: `Tool: ${header}`, description: t('agentPreview.functionCallingSkill'), colorClass: 'sec-tools' };
  if (parentSection === 'sec-org')
    return { label: `Tenant: ${header}`, description: t('vault.share.orgTab'), colorClass: 'sec-org' };

  // Return null for unrecognized headers — they should NOT start a new section
  return null;
}

function parseSections(content: string, t: TFunc): Section[] {
  if (!content.trim()) return [];

  const lines = content.split('\n');
  const sections: Section[] = [];
  let currentLabel = t('agentPreview.systemPrompt');
  let currentDesc = t('agentPreview.yourAgentPersonality');
  let currentClass = 'sec-prompt';
  let currentLines: string[] = [];
  let parentClass = '';
  let insideCodeBlock = false;
  let insideSystemPrompt = false;

  const flush = () => {
    const text = currentLines.join('\n').trim();
    if (text) {
      sections.push({ label: currentLabel, description: currentDesc, content: text, colorClass: currentClass });
    }
    currentLines = [];
  };

  for (const line of lines) {
    // FIRST: detect recognized platform `## ` headers regardless of
    // code-block state. A chatbot persona may contain an unbalanced ```
    // (the JewishPro author left a stray one, for example) — without
    // this priority check, every later section would silently fold into
    // the prior one because `insideCodeBlock` got stuck on. Platform
    // headers are reserved names; the chatbot's persona content will not
    // legitimately use these inside a code block.
    if (line.startsWith('## ')) {
      const header = line.substring(3).trim();
      const info = classifyHeader(header, '', t);
      if (info) {
        insideCodeBlock = false;
        flush();
        currentLabel = info.label;
        currentDesc = info.description;
        currentClass = info.colorClass;
        parentClass = info.colorClass;
        insideSystemPrompt = info.colorClass === 'sec-prompt';
        continue;
      }
      // Unknown `## ` — fall through to code-block / content handling.
    }

    // Track code blocks — don't parse `### ` sub-headers inside them
    if (line.trim().startsWith('```')) {
      insideCodeBlock = !insideCodeBlock;
      currentLines.push(line);
      continue;
    }

    if (insideCodeBlock) {
      currentLines.push(line);
      continue;
    }

    if (line.startsWith('## ')) {
      // Unknown `## ` header that fell through above — push as content.
      currentLines.push(line);
    } else if (line.startsWith('### ') && !insideSystemPrompt && currentClass !== 'sec-modes') {
      // Sub-headers split sections only in known platform sections (reference docs, knowledge sources, tools).
      // Inside the system prompt, ### is user content — don't split.
      // Inside the Behavioral Modes catalog (sec-modes), ### sub-headers
      // like "Available modes" and "Rules" are part of the catalog
      // body, NOT new sections — they would otherwise split off (and
      // `### Rules` would be misclassified as the platform "Hard Agent
      // Rules" section, rendering grey instead of pink).
      flush();
      const subHeader = line.substring(4).trim();
      const info = classifyHeader(subHeader, parentClass, t);
      if (info) {
        currentLabel = info.label;
        currentDesc = info.description;
        currentClass = info.colorClass;
      } else {
        currentLabel = subHeader;
        currentDesc = '';
        currentClass = parentClass || 'sec-item';
      }
      // Sub-header is shown in the section label strip — don't duplicate inside the block.
    } else if (parentClass === 'sec-skills' && line.startsWith('- **')) {
      // Each skill gets its own block
      flush();
      const match = line.match(/- \*\*([^*]+)\*\*/);
      const skillName = match ? match[1] : 'Skill';
      currentLabel = `Skill: ${skillName}`;
      currentDesc = t('agentPreview.behavioralSubAgent');
      currentClass = 'sec-skills';
      currentLines.push(line);
    } else {
      currentLines.push(line);
    }
  }

  flush();
  return sections;
}

export const AgentPreview: React.FC<AgentPreviewProps> = ({
  content,
  title,
  subtitle,
  defaultCollapsed = true,
  onRefresh,
  refreshing,
}) => {
  const { t } = useLanguage();
  const resolvedTitle = title ?? t('agentPreview.title');
  const resolvedSubtitle = subtitle ?? t('agentPreview.subtitle');
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [rawMode, setRawMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const sections = useMemo(() => parseSections(content, t), [content, t]);

  if (!content.trim()) return null;

  return (
    <div className="agent-preview-section">
      <button
        type="button"
        className="agent-preview-toggle"
        onClick={() => setCollapsed(!collapsed)}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          className={`agent-preview-chevron ${collapsed ? '' : 'expanded'}`}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <span>{resolvedTitle}</span>
        <span className="agent-preview-hint">{resolvedSubtitle}</span>
        {onRefresh && !collapsed && (
          <button
            type="button"
            className="agent-preview-refresh"
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            disabled={refreshing}
            title={t('agentPreview.refresh')}
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" className={refreshing ? 'spinning' : ''}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </button>
        )}
      </button>
      {!collapsed && (
        <>
          <div className="agent-preview-toolbar">
            <button
              type="button"
              className={`agent-preview-mode-btn ${!rawMode ? 'active' : ''}`}
              onClick={() => setRawMode(false)}
            >{t('agentPreview.labeled')}</button>
            <button
              type="button"
              className={`agent-preview-mode-btn ${rawMode ? 'active' : ''}`}
              onClick={() => setRawMode(true)}
            >Raw</button>
            <button
              type="button"
              className="agent-preview-expand-btn"
              onClick={() => setExpanded(true)}
              title={t('agentPreview.openFullScreen')}
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
          </div>
          {rawMode ? (
            <pre className="agent-preview-raw">{content}</pre>
          ) : (
            <div className="agent-preview-sections">
              {sections.map((section, i) => (
                <div key={i} className={`agent-preview-block ${section.colorClass}`}>
                  <div className="agent-preview-block-header">
                    <span className="agent-preview-block-label" dir="auto">{section.label}</span>
                    {section.description && (
                      <span dir="auto" className="agent-preview-block-desc">— {section.description}</span>
                    )}
                  </div>
                  <div className="agent-preview-block-content agent-preview-md">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {expanded && ReactDOM.createPortal(
        <div className="agent-preview-fullscreen-overlay">
          <div className="agent-preview-fullscreen" onClick={(e) => e.stopPropagation()}>
            <div className="agent-preview-fullscreen-header">
              <h3>{resolvedTitle}</h3>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <button
                  type="button"
                  className={`agent-preview-mode-btn ${!rawMode ? 'active' : ''}`}
                  onClick={() => setRawMode(false)}
                >{t('agentPreview.labeled')}</button>
                <button
                  type="button"
                  className={`agent-preview-mode-btn ${rawMode ? 'active' : ''}`}
                  onClick={() => setRawMode(true)}
                >Raw</button>
                <button
                  type="button"
                  className="agent-preview-fullscreen-close"
                  onClick={() => setExpanded(false)}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="agent-preview-fullscreen-body">
              {rawMode ? (
                <pre className="agent-preview-raw">{content}</pre>
              ) : (
                <div className="agent-preview-sections">
                  {sections.map((section, i) => (
                    <div key={i} className={`agent-preview-block ${section.colorClass}`}>
                      <div className="agent-preview-block-header">
                        <span className="agent-preview-block-label" dir="auto">{section.label}</span>
                        {section.description && (
                          <span dir="auto" className="agent-preview-block-desc">— {section.description}</span>
                        )}
                      </div>
                      <div className="agent-preview-block-content agent-preview-md">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
